import { parse as defaultParse } from "regexparam";
import type { Parser } from "wouter";

/**
 * Wouter v3 / regexparam v3 doesn't recognize `@:param` as a parameterized
 * segment — it treats the entire `@:handle` as a literal string, so routes
 * like `/@:handle/:slug` never match real `/@anthony/...` URLs and quietly
 * fall through to `<Route component={NotFound} />`.
 *
 * This parser delegates to the default for everything except paths whose
 * segments start with `@:`. For those, we hand-build the regex so the `@`
 * is a literal prefix and the rest of the segment is captured as the param.
 */
export const wouterParser: Parser = (route, loose) => {
  if (typeof route !== "string") return defaultParse(route as any, loose);
  const str = route;
  if (!/\/@:/.test(str)) return defaultParse(str, loose);

  const keys: string[] = [];
  let pattern = "^";
  for (const segment of str.split("/").filter(Boolean)) {
    if (segment.startsWith("@:")) {
      const optional = segment.endsWith("?");
      const name = segment.slice(2, optional ? -1 : undefined);
      keys.push(name);
      // For optional @-segments the slash, @, and capture must be one
      // grouped optional unit — naively appending "\/" first and then
      // an optional capture lets the slash and @ be independently
      // optional, which would match unintended paths.
      pattern += optional ? "(?:\\/@([^/]+))?" : "\\/@([^/]+?)";
      continue;
    }
    pattern += "\\/";
    if (segment.startsWith(":")) {
      const optional = segment.endsWith("?");
      const wildcard = segment.endsWith("*");
      const name = segment.slice(1, optional || wildcard ? -1 : undefined);
      keys.push(name);
      if (wildcard) pattern += "(.+)";
      else if (optional) pattern += "?([^/]*?)";
      else pattern += "([^/]+?)";
    } else if (segment === "*") {
      keys.push("wild");
      pattern += "(.*)";
    } else {
      pattern += segment.replace(/[.+*?^$()|[\]{}\\]/g, "\\$&");
    }
  }
  pattern += loose ? "(?=$|\\/)" : "\\/?$";
  return { pattern: new RegExp(pattern, "i"), keys };
};
